import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { confirmSignUp, resendSignUp } from '../services/cognitoService';
import AuthLayout from '../components/layout/AuthLayout';
import { BRAND, fieldSx, outlineButtonSx, primaryButtonSx, textButtonSx } from '../styles/ui';

const VerifyAccount: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Get email from URL params, location state, or session storage
  const emailFromParams = searchParams.get('email') || '';
  const [email, setEmail] = useState(
    emailFromParams || 
    (location.state as { email?: string })?.email || 
    sessionStorage.getItem('pendingVerificationEmail') || 
    ''
  );

  // Handle back button and email persistence
  useEffect(() => {
    // Save email to session storage when it's available
    if (email) {
      sessionStorage.setItem('pendingVerificationEmail', email);
    } else if (!initialLoad) {
      // Only redirect if we're sure there's no email after initial load
      navigate('/login', { 
        state: { 
          error: 'Please log in again to verify your account',
          from: 'verify-account'
        } 
      });
    }
    
    setInitialLoad(false);
    
    // Clean up on unmount
    return () => {
      // Only clear if we're not navigating to login
      if (window.location.pathname !== '/login') {
        sessionStorage.removeItem('pendingVerificationEmail');
      }
    };
  }, [email, navigate, initialLoad]);

  // Form validation schema
  const validationSchema = Yup.object({
    email: Yup.string()
      .email('Enter a valid email')
      .required('Email is required'),
    code: Yup.string()
      .required('Verification code is required')
      .length(6, 'Verification code must be 6 characters')
  });

  // Formik setup
  const formik = useFormik({
    initialValues: {
      email: email, // Initialize with email from URL or state
      code: ''
    },
    enableReinitialize: true, // Update form if email changes
    validationSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      setError(null);
      
      try {
        await confirmSignUp(values.email, values.code);
        // Redirect to login with success message
        window.location.href = `/login?verified=true&email=${encodeURIComponent(values.email)}`;
      } catch (err: any) {
        console.error('Verification error:', err);
        setError(err.message || 'Failed to verify account. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
  });

  // Send verification code on first load
  useEffect(() => {
    if (email && initialLoad) {
      const verificationSent = sessionStorage.getItem(`verification_sent_${email}`);
      const justRegistered = sessionStorage.getItem(`just_registered_${email}`);
      
      // Only send verification code if we just registered or haven't sent one yet
      if (justRegistered || !verificationSent) {
        // Clear the just_registered flag
        if (justRegistered) {
          sessionStorage.removeItem(`just_registered_${email}`);
        }
        
        // Mark that we've sent a verification code for this email
        sessionStorage.setItem(`verification_sent_${email}`, 'true');
        
        // Send the verification code
        resendSignUp(email).catch(err => {
          console.error('Error sending verification code:', err);
          if (err.name === 'LimitExceededException') {
            setError('Too many attempts. Please wait before requesting a new code.');
          } else {
            setError('Failed to send verification code. Please try again.');
          }
        });
      }
      
      setInitialLoad(false);
    } else if (!email) {
      console.log('No email provided, redirecting to login');
      window.location.href = '/login?error=no_email';
    }
  }, [email, initialLoad]);

  // Manual resend verification code
  const handleResendCode = async () => {
    if (!email) return;
    
    try {
      setIsResending(true);
      setError(null);
      await resendSignUp(email);
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (err: any) {
      console.error('Error resending verification code:', err);
      if (err.name === 'LimitExceededException') {
        setError('Too many attempts. Please wait before requesting a new code.');
      } else {
        setError('Failed to resend verification code. Please try again.');
      }
    } finally {
      setIsResending(false);
    }
  };

  return (
    <AuthLayout
      title="Verify Your Account"
      footerStart={
        <Button
          onClick={handleResendCode}
          disabled={isResending || !email}
          sx={textButtonSx}
        >
          {isResending ? 'Sending...' : 'Resend Code'}
        </Button>
      }
      footerActions={
        <>
          <Button onClick={() => navigate('/login')} variant="outlined" sx={outlineButtonSx}>
            Back to Login
          </Button>
          <Button
            type="submit"
            form="verify-form"
            variant="contained"
            disabled={isLoading || !formik.isValid}
            sx={primaryButtonSx}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Verify Account'}
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 4, backgroundColor: BRAND.selected, p: 3, borderRadius: 1 }}>
        <InfoOutlinedIcon sx={{ color: BRAND.accent, mt: 0.25 }} />
        <Typography variant="body2" sx={{ color: BRAND.ink }}>
          Please enter the verification code sent to your email.
        </Typography>
      </Box>

      {resendSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Verification code resent successfully.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <form id="verify-form" onSubmit={formik.handleSubmit} noValidate>
        <TextField
          margin="normal"
          required
          fullWidth
          id="email"
          label="Email Address"
          name="email"
          autoComplete="email"
          autoFocus={!email}
          value={email || formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email ? String(formik.errors.email) : ''}
          disabled={!!email}
          sx={{
            ...fieldSx,
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: BRAND.ink,
              color: BRAND.ink,
              fontWeight: 'bold',
              opacity: 1
            },
            '& .MuiInputBase-root.Mui-disabled': {
              backgroundColor: BRAND.inset
            }
          }}
        />

        <TextField
          margin="normal"
          required
          fullWidth
          name="code"
          label="Verification Code"
          type="text"
          id="code"
          autoComplete="one-time-code"
          inputProps={{ inputMode: 'numeric' }}
          value={formik.values.code}
          onChange={(e) => {
            formik.handleChange(e);
            formik.setFieldTouched('code', true, false);
          }}
          onBlur={formik.handleBlur}
          error={formik.touched.code && Boolean(formik.errors.code)}
          helperText={formik.touched.code && formik.errors.code ? String(formik.errors.code) : ''}
          sx={fieldSx}
        />
      </form>
    </AuthLayout>
  );
};

export default VerifyAccount;
